<script lang="ts">
  import FlameIcon from '~icons/tabler/flame'
  import Tooltip from './tooltip/Tooltip.svelte'
  interface Props {
    streakDays: number
    format?: 'none' | 'short' | 'long'
    size?: 'small' | 'default'
    infoTooltip?: boolean
    requiresSessionToday?: boolean | undefined
  }

  let {
    streakDays,
    format = 'short',
    size = 'default',
    infoTooltip = false,
    requiresSessionToday = false,
  }: Props = $props()

  const animationDuration = 950 + Math.round(Math.random() * 100)

  let hasInfo = $derived(
    format !== 'none' && (format === 'long' || streakDays > 0),
  )

  let prev: string | undefined = $derived.by(() => {
    if (streakDays < 10) return 'streak-0'
    if (streakDays < 20) return 'streak-10'
    if (streakDays < 30) return 'streak-20'
    return 'max-streak streak-30'
  })
  let next: string | undefined = $derived.by(() => {
    if (streakDays < 10) return 'streak-10'
    if (streakDays < 20) return 'streak-20'
    if (streakDays < 30) return 'streak-30'
    return undefined
  })
  let progress = $derived.by(() => {
    if (streakDays === 0) return 0
    else if (streakDays >= 30) return 1
    else return (streakDays % 10) / 10
  })

  let clipStart = $derived.by(() => {
    let thisProgress = progress
    if (streakDays < 10) {
      if (streakDays === 1) thisProgress = 0
      else thisProgress = (streakDays - 1) / 9
    }
    if (thisProgress === 0) return 100
    if (thisProgress === 1) return 0

    let safe = 20
    return 100 - safe - (100 - safe * 2) * thisProgress - safe / 2
  })
</script>

{#snippet info()}
  {#if format === 'short' && streakDays > 0}
    <span class="streak-info">{streakDays}</span>
  {/if}
  {#if format === 'long'}
    <span class="streak-info">
      {#if streakDays > 0}
        {streakDays} day streak
      {:else}
        Not on a streak
      {/if}
      {#if requiresSessionToday === true}
        <div class="keepalive-notice">
          Complete a session today to keep your streak!
        </div>
      {/if}
    </span>
  {/if}
{/snippet}

<div
  class="streak size-{size}"
  style:--progress={progress}
  style:--animation-duration={`${animationDuration}ms`}
>
  {#if prev}
    <div class="icon prev {prev}">
      <FlameIcon />
    </div>
  {/if}
  {#if next}
    <div
      class="icon next {next}"
      style="mask-image: linear-gradient(to bottom, rgba(0, 0, 0, 0) {clipStart}%, rgba(0, 0, 0, 1)  {clipStart +
        20}%);"
    >
      <!-- style="clip-path: inset({clipStart}% 0 0 0);" -->
      <FlameIcon />
    </div>
  {/if}

  {#if hasInfo}
    {#if infoTooltip}
      <Tooltip style="status">{@render info()}</Tooltip>
    {:else}
      {@render info()}
    {/if}
  {/if}
</div>

<style lang="postcss">
  @keyframes whobble {
    0% {
      transform: rotate(-12deg);
    }
    50% {
      transform: rotate(12deg);
    }
    100% {
      transform: rotate(-12deg);
    }
  }
  @keyframes rainbow-fill {
    0% {
      fill: var(--color-bg-accent);
    }
    33% {
      fill: var(--color-text-brand);
    }
    66% {
      fill: var(--color-bg-success);
    }
    100% {
      fill: var(--color-bg-accent);
    }
  }
  @keyframes rainbow-stroke {
    0% {
      stroke: var(--color-bg-accent);
    }
    33% {
      stroke: var(--color-text-brand);
    }
    66% {
      stroke: var(--color-bg-success);
    }
    100% {
      stroke: var(--color-bg-accent);
    }
  }
  .streak {
    --_size: 1.5em;

    display: inline-flex;
    position: relative;
    flex-shrink: 0;
    align-items: center;
    vertical-align: middle;
    width: var(--_size);
    height: var(--_size);
    color: var(--color-text-brand);
    font-weight: normal;
    font-family: var(--font-text);

    &.size-small {
      & .streak-info {
        font-size: 0.8em;
      }
    }

    & .streak-info {
      font-weight: var(--weight-normal);
    }
    & .icon {
      position: absolute;
      inset: 0;

      --_strong-stroke-width: 2px;

      & :global(svg) {
        display: block;
        width: 100%;
        height: 100%;
      }
      & :global(svg path) {
        stroke: var(--color-text-accent);
        stroke-width: 1.5px;
      }

      &.streak-10 {
        & :global(path) {
          fill: var(--color-bg-accent-secondary);
          stroke: var(--color-text-accent);
        }
      }
      &.streak-20 {
        & :global(path) {
          animation: rainbow-stroke calc(var(--animation-duration) * 2) linear
            infinite;
          fill: var(--color-bg-accent-secondary);
          stroke-width: var(--_strong-stroke-width);
        }
      }
      &.streak-30 {
        & :global(path) {
          animation:
            rainbow-stroke calc(var(--animation-duration) * 2) linear infinite,
            rainbow-fill calc(var(--animation-duration) * 2) linear infinite;
          stroke-width: var(--_strong-stroke-width);
        }
      }
      &.max-streak {
        & :global(svg) {
          transform-origin: center 90%;
          animation: whobble calc(var(--animation-duration) * 1.3) linear
            infinite;
          stroke-width: var(--_strong-stroke-width);
        }
      }
    }
  }

  .keepalive-notice {
    max-width: 19ch;
    font-weight: bold;
  }
</style>
